import {
  parseEndpointsFromPipeList,
  parseEndpointsListFromXML,
} from '../../components/content-utils/content-parsers';
import { isMongoId } from '../context/EntityResolverContext';
import { normalizeToolName } from './tool-config';

export type InlineToolEntityListItem = {
  key: string;
  type: 'endpoint' | 'webpage';
  id?: string;
  method?: string;
  url: string;
  location: string;
  description?: string;
};

type ParsedListEntry = Omit<InlineToolEntityListItem, 'key' | 'location'>;

const ENDPOINT_LIST_TOOLS = new Set([
  'search-endpoints',
  'search-tracked-endpoints',
  'get-endpoints-from-webpage',
  'list-endpoints-on-web-page',
]);

const WEBPAGE_LIST_TOOLS = new Set(['get-webpages-from-endpoint']);

const PIPE_WEBPAGE_LINE = /^([a-f0-9]{24})\s*\|\s*(\S+)(?:\s+-\s+(.*))?$/i;

const EMPTY_DESCRIPTION = 'No description available';

function toOptionalDescription(value: string | undefined): string | undefined {
  const trimmed = value?.trim();

  return trimmed && trimmed !== EMPTY_DESCRIPTION ? trimmed : undefined;
}

function parseEndpointEntries(output: string): ParsedListEntry[] {
  const endpoints =
    parseEndpointsListFromXML(output) ?? parseEndpointsFromPipeList(output);

  return (endpoints ?? []).flatMap((endpoint) => {
    const url = endpoint.endpointUrl ?? endpoint.path;

    if (!url) {
      return [];
    }

    return [
      {
        type: 'endpoint' as const,
        id: endpoint.endpointId,
        method: endpoint.method?.toUpperCase(),
        url,
        description: toOptionalDescription(endpoint.endpointDescription),
      },
    ];
  });
}

function parseWebpageEntries(output: string): ParsedListEntry[] {
  return output
    .split('\n')
    .map((line) => line.trim().match(PIPE_WEBPAGE_LINE))
    .flatMap((match) =>
      match
        ? [
            {
              type: 'webpage' as const,
              id: match[1],
              url: match[2],
              description: toOptionalDescription(match[3]),
            },
          ]
        : [],
    );
}

function splitUrl(url: string): { host?: string; path: string } {
  if (url.startsWith('/')) {
    return { path: url };
  }

  try {
    const parsed = new URL(url);

    return {
      host: parsed.host,
      path: `${parsed.pathname}${parsed.search}` || '/',
    };
  } catch {
    return { path: url };
  }
}

function withLocations(entries: ParsedListEntry[]): InlineToolEntityListItem[] {
  const parts = entries.map((entry) => splitUrl(entry.url));
  const hosts = new Set(
    parts.flatMap((part) => (part.host ? [part.host] : [])),
  );
  const showHost = hosts.size > 1;

  return entries.map((entry, index) => {
    const { host, path } = parts[index];

    return {
      ...entry,
      id: entry.id && isMongoId(entry.id) ? entry.id : undefined,
      key: `${entry.id ?? entry.url}-${index}`,
      location: showHost && host ? `${host}${path}` : path,
    };
  });
}

export function getInlineToolEntityList({
  toolName,
  output,
}: {
  toolName: string;
  output: string | undefined;
}): InlineToolEntityListItem[] | undefined {
  if (!output) {
    return undefined;
  }

  const normalizedName = normalizeToolName(toolName);
  const entries = ENDPOINT_LIST_TOOLS.has(normalizedName)
    ? parseEndpointEntries(output)
    : WEBPAGE_LIST_TOOLS.has(normalizedName)
      ? parseWebpageEntries(output)
      : [];

  return entries.length > 0 ? withLocations(entries) : undefined;
}
